import { useEffect, useMemo, useState } from "react";
import { useCategories } from "@/contexts/CategoriesContext";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/api";
import { FileText, Download } from "lucide-react";

export default function About() {
  const { categories } = useCategories();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProducts([]));
  }, []);

  const groups = useMemo(() => {
    const withCoa = products.filter((p) => (p.coa_url || "").trim());
    const order = new Map((categories || []).map((c, i) => [c.slug, i]));
    const byCat = new Map();
    for (const p of withCoa) {
      const k = p.category || "other";
      if (!byCat.has(k)) byCat.set(k, []);
      byCat.get(k).push(p);
    }
    const result = [];
    for (const [slug, items] of byCat.entries()) {
      const cat = (categories || []).find((c) => c.slug === slug);
      result.push({
        slug,
        title: cat?.title || slug,
        order: order.has(slug) ? order.get(slug) : 999,
        items: items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      });
    }
    result.sort((a, b) => a.order - b.order);
    return result;
  }, [products, categories]);

  return (
    <div
      data-testid="about-page"
      className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24"
    >
      <div id="coa" className="grid lg:grid-cols-12 gap-10 items-end" data-testid="coa-section">
        <div className="lg:col-span-7">
          <div className="overline mb-3">Certificates of analysis</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tighter leading-tight">
            Every peptide, independently verified.
          </h1>
        </div>
        <div className="lg:col-span-5">
          <p className="text-sm text-muted-foreground leading-relaxed max-w-md">
            Each batch ships with a third-party Certificate of Analysis (HPLC purity, identity by
            mass spec, endotoxin and residual solvents). Download the latest COA for any product
            below.
          </p>
        </div>
      </div>

      <div className="mt-10 lg:mt-14 space-y-12">
        {groups.length === 0 && (
          <div className="border border-border bg-white p-10 text-center text-sm text-muted-foreground">
            COAs are being uploaded. Please check back shortly.
          </div>
        )}

        {groups.map((g) => (
          <section key={g.slug} data-testid={`coa-group-${g.slug}`}>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-xl lg:text-2xl font-semibold tracking-tight">
                {g.title}
              </h2>
              <div className="overline text-[10px]">
                {g.items.length} certificate{g.items.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.items.map((p) => (
                <a
                  key={p.product_id}
                  href={resolveImageUrl(p.coa_url)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`coa-${p.slug}`}
                  className="group border border-border bg-white p-5 lift flex items-start gap-4"
                >
                  <div className="shrink-0 w-12 h-12 border border-border bg-secondary grid place-items-center">
                    <FileText className="w-5 h-5 text-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-base tracking-tight truncate">
                      {p.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {p.presentation}
                      {p.dose ? ` · ${p.dose}` : ""}
                    </div>
                    <div className="text-xs mt-3 inline-flex items-center gap-1.5 text-foreground group-hover:underline">
                      <Download className="w-3 h-3" /> Download PDF
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
